// src/components/Employee/MesTaches.jsx
"use client"

import { useState, useEffect, useCallback } from "react"
import {
  FaCheck,
  FaClock,
  FaTrash,
  FaSpinner,
  FaFilter,
  FaSort
} from "react-icons/fa"
import { AlertCircle, Calendar } from "lucide-react"

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`

export default function MesTaches() {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [filter, setFilter] = useState("all")
  const [sortBy, setSortBy] = useState("deadline")
  const [updatingTask, setUpdatingTask] = useState(null)

  const [showDeletePopup, setShowDeletePopup] = useState(false)
  const [taskToDelete, setTaskToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const getToken = () => localStorage.getItem("access_token")
  const getUserData = () => JSON.parse(localStorage.getItem("auth_data") || "{}")

  const fetchTasks = useCallback(async () => {
    try {
      const token = getToken()
      if (!token) {
        setError("Vous devez être connecté")
        setLoading(false)
        return
      }

      const userData = getUserData()

      let allTasks = []
      let page = 1
      let hasMore = true

      while (hasMore) {
        const url = `${API_URL}/taches/?page=${page}&page_size=100`

        const response = await fetch(url, {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        })

        if (!response.ok) throw new Error("Erreur de chargement")

        const data = await response.json()

        let pageTasks = []
        if (data.results) {
          pageTasks = data.results
          if (data.next) {
            page++
          } else {
            hasMore = false
          }
        } else if (Array.isArray(data)) {
          pageTasks = data
          hasMore = false
        } else {
          hasMore = false
        }

        allTasks = [...allTasks, ...pageTasks]
      }

      const sortedTasks = allTasks.sort((a, b) => {
        const dateA = new Date(a.created_at || 0)
        const dateB = new Date(b.created_at || 0)
        return dateB - dateA
      })

      const userTasks = sortedTasks.filter(task =>
        task.responsable === userData.userId ||
        task.responsable_nom === userData.name
      )

      setTasks(userTasks)
    } catch (err) {
      console.error("Erreur:", err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTasks()
  }, [fetchTasks])

  const handleMarkAsDone = async (taskId) => {
    setUpdatingTask(taskId)
    try {
      const token = getToken()

      const response = await fetch(`${API_URL}/taches/${taskId}/changer_statut/`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ statut: "terminee" })
      })

      let responseData
      const contentType = response.headers.get("content-type")
      if (contentType && contentType.includes("application/json")) {
        responseData = await response.json()
      } else {
        const text = await response.text()
        if (response.ok) {
          responseData = { success: true }
        } else {
          responseData = { error: text }
        }
      }

      if (response.ok) {
        setTasks(prevTasks => {
          const updatedTasks = prevTasks.map(task =>
            task.id === taskId ? { ...task, statut: "terminee" } : task
          )
          return [...updatedTasks]
        })
        window.dispatchEvent(new CustomEvent('notifications-updated'))
      } else {
        console.error("Erreur mise à jour statut:", responseData)
        alert(`Erreur: ${responseData.error || "Erreur inconnue"}`)
      }
    } catch (err) {
      console.error("Erreur réseau:", err)
      alert("Erreur réseau lors de la mise à jour du statut")
    } finally {
      setUpdatingTask(null)
    }
  }

  const handleDeleteClick = (task) => {
    setTaskToDelete(task)
    setShowDeletePopup(true)
  }

  const handleConfirmDelete = () => {
    if (!taskToDelete) return

    setDeleting(true)
    setTimeout(() => {
      setTasks(prevTasks => prevTasks.filter(t => t.id !== taskToDelete.id))
      setShowDeletePopup(false)
      setTaskToDelete(null)
      setDeleting(false)
      window.dispatchEvent(new CustomEvent('notifications-updated'))
    }, 500)
  }

  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => t.statut !== 'terminee').length,
    done: tasks.filter(t => t.statut === 'terminee').length,
    late: tasks.filter(t => t.statut !== 'terminee' && t.deadline && new Date(t.deadline) < new Date()).length
  }

  const filteredTasks = tasks
    .filter(task => {
      if (filter === "all") return true
      if (filter === "pending") return task.statut !== 'terminee'
      if (filter === "done") return task.statut === 'terminee'
      if (filter === "late") return task.statut !== 'terminee' && task.deadline && new Date(task.deadline) < new Date()
      return true
    })
    .sort((a, b) => {
      if (sortBy === "deadline") {
        if (!a.deadline) return 1
        if (!b.deadline) return -1
        return new Date(a.deadline) - new Date(b.deadline)
      }
      const priorityWeight = { critique: 4, haute: 3, moyenne: 2, basse: 1 }
      return (priorityWeight[b.priorite] || 0) - (priorityWeight[a.priorite] || 0)
    })

  const getStatusBadgeStyle = (statut) => {
    const styles = {
      a_faire: { bg: '#f3f4f6', color: '#4b5563', text: 'À faire' },
      en_cours: { bg: '#dbeafe', color: '#2563eb', text: 'En cours' },
      terminee: { bg: '#dcfce7', color: '#16a34a', text: 'Terminée' }
    }
    return styles[statut] || styles.a_faire
  }

  const getPriorityBadgeStyle = (priorite) => {
    const styles = {
      critique: { bg: '#fee2e2', color: '#ef4444' },
      haute: { bg: '#ffedd5', color: '#f97316' },
      moyenne: { bg: '#fef3c7', color: '#eab308' },
      basse: { bg: '#e6f7e6', color: '#16a34a' }
    }
    return styles[priorite] || styles.moyenne
  }

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p>Chargement de vos tâches...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.errorContainer}>
          <p style={{ color: "#ef4444" }}>❌ {error}</p>
          <button onClick={fetchTasks} style={styles.retryButton}>
            Réessayer
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>MES TÂCHES</h1>
      <p style={styles.subtitle}>
        Gérez toutes vos tâches et suivez leur progression
      </p>

      <div style={styles.statsBar}>
        <div style={styles.statItem}>
          <span style={styles.statValue}>{stats.total}</span>
          <span style={styles.statLabel}>Total</span>
        </div>
        <div style={styles.statItem}>
          <span style={{ ...styles.statValue }}>{stats.pending}</span>
          <span style={styles.statLabel}>En cours</span>
        </div>
        <div style={styles.statItem}>
          <span style={{ ...styles.statValue }}>{stats.done}</span>
          <span style={styles.statLabel}>Terminées</span>
        </div>
        <div style={styles.statItem}>
          <span style={{ ...styles.statValue }}>{stats.late}</span>
          <span style={styles.statLabel}>En retard</span>
        </div>
      </div>

      <div style={styles.filterBar}>
        <div style={styles.filterGroup}>
          <FaFilter size={14} color="#666" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">Toutes les tâches ({stats.total})</option>
            <option value="pending">En cours ({stats.pending})</option>
            <option value="done">Terminées ({stats.done})</option>
            <option value="late">En retard ({stats.late})</option>
          </select>
        </div>

        <div style={styles.filterGroup}>
          <FaSort size={14} color="#666" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="deadline">Trier par échéance</option>
            <option value="priority">Trier par priorité</option>
          </select>
        </div>
      </div>

      {filteredTasks.length === 0 ? (
        <div style={styles.emptyState}>
          <FaCheck size={48} color="#9ca3af" />
          <p>Aucune tâche trouvée</p>
        </div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th>Tâche</th>
                <th>Réunion</th>
                <th>Échéance</th>
                <th>Priorité</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map((task) => {
                const late = task.statut !== 'terminee' && task.deadline && new Date(task.deadline) < new Date()
                const priorityStyle = getPriorityBadgeStyle(task.priorite)
                const statusStyle = getStatusBadgeStyle(task.statut)
                const isUpdating = updatingTask === task.id

                return (
                  <tr key={task.id}>
                    <td style={styles.taskDescription}>{task.description}</td>
                    <td>{task.reunion_titre || "—"}</td>
                    <td>
                      <span style={{
                        ...styles.deadline,
                        color: late ? "#ef4444" : "#666"
                      }}>
                        <FaClock size={12} />
                        {task.deadline ? new Date(task.deadline).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        }) : 'Pas d\'échéance'}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        ...styles.priorityBadge,
                        backgroundColor: priorityStyle.bg,
                        color: priorityStyle.color
                      }}>
                        {task.priorite}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        ...styles.statusBadge,
                        backgroundColor: statusStyle.bg,
                        color: statusStyle.color
                      }}>
                        {statusStyle.text}
                      </span>
                    </td>
                    <td>
                      <div style={styles.actions}>
                        {task.statut !== 'terminee' && (
                          <button
                            onClick={() => handleMarkAsDone(task.id)}
                            disabled={isUpdating}
                            style={{
                              ...styles.doneButton,
                              opacity: isUpdating ? 0.7 : 1,
                              cursor: isUpdating ? 'not-allowed' : 'pointer'
                            }}
                            title="Marquer comme terminée"
                          >
                            {isUpdating ? <FaSpinner className="animate-spin" size={14} /> : <FaCheck size={14} />}
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteClick(task)}
                          style={styles.deleteButton}
                          title="Supprimer"
                        >
                          <FaTrash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {tasks.length > 0 && (
            <div style={styles.totalIndicator}>
              Total: {tasks.length} tâche{tasks.length > 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}

      {/* Popup de confirmation de suppression */}
      {showDeletePopup && taskToDelete && (
        <div style={styles.overlay}>
          <div style={styles.deletePopup}>
            <div style={styles.deleteIcon}>
              <AlertCircle size={40} color="#ef4444" />
            </div>
            <h3 style={styles.deleteTitle}>Confirmer la suppression</h3>
            <p style={styles.deleteMessage}>
              Êtes-vous sûr de vouloir supprimer la tâche<br />
              <strong>"{taskToDelete.description}"</strong> ?
            </p>
            <div style={styles.deleteActions}>
              <button
                onClick={() => setShowDeletePopup(false)}
                style={styles.deleteCancelButton}
                disabled={deleting}
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmDelete}
                style={styles.deleteConfirmButton}
                disabled={deleting}
              >
                {deleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const styles = {
  page: {
    padding: "22px",
    background: "#f7f6ff",
    minHeight: "100vh",
    borderRadius: "16px"
  },
  title: {
    fontSize: "26px",
    color: "#1f1f2e",
    marginBottom: "4px",
    fontWeight: "500",
  },
  subtitle: {
    color: "#666",
    marginBottom: "18px",
    fontSize: "14px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    gap: "16px"
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "3px solid #f3f3f3",
    borderTop: "3px solid #5e17eb",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  },
  errorContainer: {
    textAlign: "center",
    padding: "40px",
    background: "white",
    borderRadius: "12px"
  },
  retryButton: {
    marginTop: "12px",
    padding: "8px 16px",
    background: "#5e17eb",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer"
  },
  statsBar: {
    display: "flex",
    gap: "32px",
    background: "white",
    padding: "16px 24px",
    borderRadius: "12px",
    marginBottom: "18px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    flexWrap: "wrap"
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  },
  statValue: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#1f1f2e"
  },
  statLabel: {
    fontSize: "12px",
    color: "#666",
    marginTop: "4px"
  },
  filterBar: {
    display: "flex",
    gap: "16px",
    marginBottom: "20px",
    flexWrap: "wrap"
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "white",
    padding: "4px 12px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb"
  },
  filterSelect: {
    padding: "8px",
    border: "none",
    background: "transparent",
    outline: "none",
    cursor: "pointer",
    fontSize: "14px",
    minWidth: "150px"
  },
  emptyState: {
    textAlign: "center",
    padding: "60px",
    background: "white",
    borderRadius: "12px",
    color: "#6b7280"
  },
  tableContainer: {
    background: "white",
    borderRadius: "12px",
    padding: "20px",
    overflowX: "auto"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px"
  },
  taskDescription: {
    fontWeight: "500",
    color: "#1f1f2e"
  },
  deadline: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "13px"
  },
  priorityBadge: {
    padding: "4px 8px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "500",
    textTransform: "capitalize"
  },
  statusBadge: {
    padding: "4px 8px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "500",
    display: "inline-block"
  },
  actions: {
    display: "flex",
    gap: "8px",
    justifyContent: "center"
  },
  doneButton: {
    color: "#16a34a",
    padding: "6px",
    borderRadius: "6px",
    border: "none",
    background: "#e6f7e6",
    cursor: "pointer",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "32px",
    height: "32px"
  },
  deleteButton: {
    color: "#ef4444",
    padding: "6px",
    borderRadius: "6px",
    border: "none",
    background: "#fee2e2",
    cursor: "pointer",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "32px",
    height: "32px"
  },
  totalIndicator: {
    textAlign: 'right',
    marginTop: '15px',
    padding: '8px 12px',
    fontSize: '13px',
    color: '#5e17eb',
    fontWeight: '500',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#f9f9ff'
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)',
  },
  deletePopup: {
    background: 'white',
    borderRadius: 16,
    padding: 32,
    width: '90%',
    maxWidth: 450,
    textAlign: 'center',
  },
  deleteIcon: {
    marginBottom: 16,
  },
  deleteTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: '#1f1f2e',
    marginBottom: 12,
  },
  deleteMessage: {
    fontSize: 14,
    color: '#4b5563',
    marginBottom: 8,
    lineHeight: 1.5,
  },
  deleteActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: 12,
  },
  deleteCancelButton: {
    padding: '10px 20px',
    background: '#f3f4f6',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    color: '#666',
    cursor: 'pointer',
  },
  deleteConfirmButton: {
    padding: '10px 20px',
    background: '#ef4444',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    color: 'white',
    cursor: 'pointer',
  },
}

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style")
  styleSheet.innerText = `
    @keyframes spin { 
      0% { transform: rotate(0deg); } 
      100% { transform: rotate(360deg); } 
    }
    .animate-spin {
      animation: spin 1s linear infinite;
    }
  `
  document.head.appendChild(styleSheet)
}